import L from './leaflet.js'

// What every map shares: the tiles (Mapbox through our server's proxy, which adds the token), the default centre, the
// scale control, extra panes and a fitBounds that can't throw.

/** The Netherlands, used when a map has nothing to show yet */
export const defaultCenter: L.LatLngLiteral = {lat: 52.2076823, lng: 5.1584931}
export const defaultZoom = 13

/** Zoom used to show a single point, fitBounds of one point would zoom in all the way */
export const singlePointZoom = 16

export const mapboxStyle = {account: 'veloyd', id: 'cka2lgtlz0huj1iln27ujuqim'}

/** Map options of the planner maps: canvas markers, our own controls and attribution */
export const baseMapOptions: L.MapOptions = {
    preferCanvas: true,
    boxZoom: false,
    attributionControl: false,
    zoomControl: false
}

/** The Mapbox tile layer, options are added to (or override) the defaults */
export function createTileLayer(options: L.TileLayerOptions = {}) {
    return L.tileLayer(`${window.location.origin}/api/mapbox/styles/v1/{account}/{id}/tiles/{z}/{x}/{y}`, {
        maxZoom: 22,
        maxNativeZoom: 22,
        updateWhenZooming: false,
        updateWhenIdle: true,
        tileSize: 512,
        zoomOffset: -1,
        ...mapboxStyle,
        ...options
    })
}

/** Adds the scale at the bottom left, not draggable so dragging over it pans the map instead of dragging an image */
export function addScaleControl(map: L.Map) {
    const scale = L.control.scale().addTo(map)
    const container = scale.getContainer()

    for (const node of [container.parentElement, container, ...Array.from(container.children)]) {
        node?.setAttribute('draggable', 'false')
    }

    return scale
}

export const panes = {
    // Depot icons, below the default marker pane (600) and the canvas with the stops (overlayPane, 400) so the stops
    // at a depot stay clickable
    lowMarkers: {name: 'lowMarkers', zIndex: 350},
    // Lines drawn below the markers' canvas (overlayPane, 400) whatever order they are added in
    routeLines: {name: 'routeLines', zIndex: 390}
}

export function createPane(map: L.Map, pane: {name: string, zIndex: number}) {
    const element = map.getPane(pane.name) || map.createPane(pane.name)
    element.style.zIndex = String(pane.zIndex)
    return element
}

export interface FitOptions extends L.FitBoundsOptions {
    /** Zoom for a single point, singlePointZoom by default */
    pointZoom?: number
}

/**
 * fitBounds that ignores missing and invalid points and does nothing without points (Leaflet throws on empty bounds).
 * A single point is centred at pointZoom. Returns whether the view changed.
 */
export function fitBoundsSafe(map: L.Map, points: Array<L.LatLngExpression | null | undefined> | L.LatLngBounds, options: FitOptions = {}) {
    const {pointZoom = singlePointZoom, ...fitOptions} = options

    if (points instanceof L.LatLngBounds) {
        if (!points.isValid()) {
            return false
        }

        map.fitBounds(points, fitOptions)
        return true
    }

    const latLngs = (points || []).map(toLatLng).filter(Boolean)

    if (!latLngs.length) {
        return false
    }

    const bounds = L.latLngBounds(latLngs)

    if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
        map.setView(bounds.getCenter(), pointZoom)
    } else {
        map.fitBounds(bounds, fitOptions)
    }

    return true
}

function toLatLng(point: L.LatLngExpression | null | undefined): L.LatLng | null {
    if (!point) {
        return null
    }

    try {
        const latLng = L.latLng(point)
        return latLng && Number.isFinite(latLng.lat) && Number.isFinite(latLng.lng) ? latLng : null
    } catch {
        return null
    }
}
